import { Button } from "@material-ui/core";
import EmailIcon from "@material-ui/icons/Email";
import React from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import GoogleLogo from "src/assets/2702602.png";
import Headerpage from "src/shared/components/header";
import Footerpage from "src/shared/components/footer";

interface IPendingInvitation {
  email: string;
  token: string;
  role?: string | null;
  schoolId?: number;
  linkUrl?: string | null;
  isGoogleEmail?: boolean;
  isExpired?: boolean;
}

const publicUrl = process.env.PUBLIC_URL;

const useQuery = () => new URLSearchParams(useLocation().search);

const PendingInvitePage: React.FC = () => {
  const query = useQuery();
  const queryEmail = (query.get("email") || "").trim().toLowerCase();
  const queryError = query.get("error") || "";
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [invite, setInvite] = React.useState<IPendingInvitation | null>(null);

  React.useEffect(() => {
    const resetSubmittingState = () => setSubmitting(false);
    window.addEventListener("pageshow", resetSubmittingState);
    return () => window.removeEventListener("pageshow", resetSubmittingState);
  }, []);

  React.useEffect(() => {
    const loadInvite = async () => {
      if (!queryEmail) {
        setError("Invitation email is missing.");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${publicUrl}/api/pending-invitation/${encodeURIComponent(queryEmail)}`, {
          withCredentials: true,
        });
        if (response.data?.status && response.data?.data) {
          setInvite(response.data.data);
        } else {
          setError("No pending invitation was found for this email.");
        }
      } catch (err) {
        setError("Unable to load the invitation. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    void loadInvite();
  }, [queryEmail]);

  const handleGoogleLogin = async () => {
    if (!invite) {
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      localStorage.setItem("invitationToken", invite.token);
      localStorage.setItem("invitationEmail", invite.email);
      const response = await axios.get(`${publicUrl}/api/sign-in/google`, { withCredentials: true });
      window.location.href = response.data.url;
    } catch (err: any) {
      setError(err?.response?.data?.message || "Unable to accept this invite. Please try again.");
      setSubmitting(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!invite) {
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      localStorage.setItem("invitationToken", invite.token);
      localStorage.setItem("invitationEmail", invite.email);
      window.location.href = `/invitation-password?token=${encodeURIComponent(invite.token)}&email=${encodeURIComponent(invite.email)}`;
    } catch (err) {
      setError("Unable to open the account setup page. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <Headerpage />
      <div className="container-inner">
        <div
          className="content-leader"
          style={{
            alignItems: "center",
            backgroundImage: `-webkit-gradient(linear, left top, left bottom, from(rgba(62, 124, 177, 0.5)), to(rgba(62, 124, 177, 0.5))),url(${require("src/assets/gritp.jpg")})`,
            display: "flex",
            justifyContent: "center",
            minHeight: "calc(100vh - 153px)",
            width: "100%",
          }}
        >
          <div
            className="welcome-back welcome-sign-outer"
            style={{
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: 16,
              boxShadow: "0 18px 45px rgba(15, 23, 42, 0.18)",
              color: "#172554",
              maxWidth: 520,
              padding: "28px 32px 24px",
              textAlign: "center",
              width: "calc(100% - 32px)",
            }}
          >
            <div style={{ alignItems: "center", borderBottom: "1px solid #e5e7eb", display: "flex", gap: 10, justifyContent: "center", paddingBottom: 22 }}>
              <EmailIcon style={{ color: "#172554", fontSize: 26 }} />
              <span style={{ color: "#111827", fontSize: 18, fontWeight: 700 }}>Grit Leadership</span>
            </div>
            {loading ? (
              <p style={{ color: "#475569", fontSize: 17, margin: "42px 0" }}>Loading invitation...</p>
            ) : invite ? (
              <>
                <h1 className="sign-in-head" style={{ color: "#111827", fontSize: 28, margin: "28px 0 18px" }}>You've Been Invited!</h1>
                <p style={{ color: "#6b7280", fontSize: 16, lineHeight: 1.5, margin: 0 }}>
                  Your Athletic Director has invited you to join Grit Leadership using the email address below.
                </p>
                <p style={{ color: "#6b7280", fontSize: 14, margin: "26px 0 6px" }}>Invited email address</p>
                <p style={{ color: "#111827", fontSize: 18, fontWeight: 700, margin: 0, overflowWrap: "anywhere" }}>{invite.email}</p>
                <p style={{ color: "#6b7280", fontSize: 16, lineHeight: 1.5, margin: "24px 0" }}>
                  Complete your registration using this email address to access your school's dashboard.
                </p>
                {(error || queryError) && <p style={{ color: "#b91c1c", fontSize: 14, lineHeight: 1.4 }}>{error || queryError}</p>}
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Button
                    style={{ background: "#172b72", borderRadius: 10, color: "#ffffff", fontSize: 16, fontWeight: 700, minHeight: 52, textTransform: "none" }}
                    disabled={submitting}
                    onClick={handleGoogleLogin}
                    startIcon={<img alt="" src={GoogleLogo} style={{ height: 20, width: 20 }} />}
                    variant="contained"
                  >
                    Continue with Google
                  </Button>
                  <Button
                    style={{ background: "#ffffff", border: "1px solid #d1d5db", borderRadius: 10, color: "#111827", fontSize: 16, fontWeight: 700, minHeight: 52, textTransform: "none" }}
                    disabled={submitting}
                    onClick={handleCreateAccount}
                    startIcon={<EmailIcon />}
                    variant="contained"
                  >
                    Create a Password
                  </Button>
                </div>
                <p style={{ color: "#6b7280", fontSize: 14, margin: "22px 0 0" }}>Need assistance? Contact your Athletic Director.</p>
              </>
            ) : (
              <>
                <p style={{ color: "#08045f", fontSize: 20 }}>{error}</p>
                <Button
                  className="button-signup"
                  color="primary"
                  onClick={() => { window.location.href = "/sign-in"; }}
                  variant="contained"
                >
                  Back to sign in
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <Footerpage />
    </>
  );
};

export default PendingInvitePage;



// WEBPACK FOOTER //
// ./src/features/home/pages/signIn-page/PendingInvitePage.tsx